import React, { useEffect, useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { keys } from 'ramda'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { getCinderQuotaUsage } from 'account/components/userManagement/tenants/new-actions'
import { Blueprint } from 'openstack/components/infrastructure/blueprint/model'
import { formattedValue } from 'core/utils/formatters'
import SimpleLink from 'core/components/SimpleLink'
import { routeManager } from 'core/utils/routes'

const useStyles = makeStyles<Theme>((theme) => ({
  boxes: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: 1,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  box: {
    minHeight: 88,
    padding: '16px clamp(8px, 2vw, 16px) 24px',
    background: theme.palette.grey['000'],
    display: 'flex',
    justifyContent: 'space-between',
    flexFlow: 'column',
    minWidth: 0,
    '&:first-child': {
      borderRadius: '4px 0px 0px 4px',
    },
    '&:last-child': {
      borderRadius: '0px 4px 4px 0px',
    },
  },
  boxLabel: {
    color: theme.palette.grey[500],
  },
  boxHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 34,
  },
  boxLink: {
    marginRight: 16,
    fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)',
    whiteSpace: 'nowrap',
  },
  caret: {
    marginLeft: 8,
  },
  data: {
    display: 'flex',
    alignItems: 'end',
    gap: 'clamp(8px, 2vw, 16px)',
    rowGap: 4,
    minWidth: 0,
    flexWrap: 'wrap',
  },
  dataLarge: {
    fontWeight: 400,
    fontSize: 'clamp(1.5rem, 3vw, 2.125rem)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  dataSmall: {
    color: theme.palette.grey[500],
    fontSize: 'clamp(0.625rem, 1.2vw, 0.875rem)',
    whiteSpace: 'nowrap',
  },
  detachedIcon: {
    color: '#966A00',
    marginRight: 8,
    background: theme.palette.yellow[200],
    height: 24,
    width: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
}))

interface StorageQuotas {
  capacity?: number
  capacityString?: string
  usage?: number
  usageString?: string
}

interface Props {
  blueprint: Blueprint
  volumes: any[]
}

export default function StorageDashboardHeader({ blueprint, volumes }: Props) {
  const classes = useStyles()
  const { prefs, updatePrefs } = useScopedPreferences()
  // const { allTenants } = prefs
  const { currentTenant } = prefs

  const [storageQuotas, setStorageQuotas] = useState<any>({})
  useEffect(() => {
    const makeCalls = async () => {
      const results = await getCinderQuotaUsage({ tenantId: currentTenant })
      setStorageQuotas(results)
    }
    makeCalls()
  }, [currentTenant])

  const storageUsage = useMemo<StorageQuotas>(() => {
    if (!storageQuotas?.gigabytes) {
      return {}
    }
    const capacity = storageQuotas?.gigabytes?.limit
    const usage = storageQuotas?.gigabytes?.in_use
    const capBytes = capacity * 1024 * 1024 * 1024
    const usageBytes = usage * 1024 * 1024 * 1024
    const percent = capacity === -1 ? 0 : Math.round((usage / capacity) * 100)
    const baseUsageString =
      usage >= 1024 ? formattedValue(usageBytes, 'TB', 1) : formattedValue(usageBytes, 'GB', 1)
    const usageString =
      capacity === -1 ? `${baseUsageString} used` : `${baseUsageString} used (${percent}%)`
    return {
      capacity,
      capacityString:
        capacity === -1
          ? 'unlimited'
          : capacity >= 1024
          ? formattedValue(capBytes, 'TB', 1)
          : formattedValue(capBytes, 'GB', 1),
      usage,
      usageString,
    }
  }, [storageQuotas])

  // Todo?: showing volume backends connected to hosts requires checking cinder
  // config of every storage host, is it possible to get a bulk API call from
  // backend for this?
  const volumeBackends = useMemo(() => {
    if (!blueprint?.storageBackends) {
      return []
    }
    return keys(blueprint?.storageBackends)
  }, [blueprint])

  // Count number of attached vs detached volumes
  const volumeCounts = useMemo(() => {
    const counts = volumes.reduce(
      (accum, volume) => {
        if (volume?.attachments?.length) {
          return {
            ...accum,
            active: accum.active + 1,
          }
        } else if (!volume?.attachments?.length) {
          return {
            ...accum,
            detached: accum.detached + 1,
          }
        }
        return accum
      },
      {
        active: 0,
        detached: 0,
      },
    )
    return counts
  }, [volumes])

  return (
    <div className={classes.boxes}>
      <div className={classes.box}>
        <div className={classes.boxHeader}>
          <Text variant="caption1" className={classes.boxLabel}>
            Total Capacity
          </Text>
        </div>
        <div className={classes.data}>
          <Text variant="h2" className={classes.dataLarge}>
            {storageUsage?.capacity === -1 ? (
              <FontAwesomeIcon>infinity</FontAwesomeIcon>
            ) : (
              storageUsage?.capacityString
            )}
          </Text>
          <Text variant="body1" className={classes.dataSmall}>
            {storageUsage?.usageString}
          </Text>
        </div>
      </div>
      <div className={classes.box}>
        <div className={classes.boxHeader}>
          <Text variant="caption1" className={classes.boxLabel}>
            Volume Backends
          </Text>
        </div>
        <div className={classes.data}>
          <Text variant="h2" className={classes.dataLarge}>
            {volumeBackends?.length}
          </Text>
          <Text variant="body1" className={classes.dataSmall}>
            Backends Configured
          </Text>
        </div>
      </div>
      <div className={classes.box}>
        <div className={classes.boxHeader}>
          <Text variant="caption1" className={classes.boxLabel}>
            Attached Volumes
          </Text>
          <SimpleLink src={routeManager.routes.openstack.volumes.path()}>
            <Text variant="caption1" className={classes.boxLink}>
              See All{' '}
              <FontAwesomeIcon className={classes.caret} size="md" regular>
                chevron-right
              </FontAwesomeIcon>
            </Text>
          </SimpleLink>
        </div>
        <div className={classes.data}>
          <Text variant="h2" className={classes.dataLarge}>
            {volumeCounts?.active}
          </Text>
          <Text variant="body1" className={classes.dataSmall}>
            Attached to VMs
          </Text>
        </div>
      </div>
      <div className={classes.box}>
        <div className={classes.boxHeader}>
          <Text variant="caption1" className={classes.boxLabel}>
            Detached Volumes
          </Text>
          <SimpleLink src={routeManager.routes.openstack.volumes.path()}>
            <Text variant="caption1" className={classes.boxLink}>
              See All{' '}
              <FontAwesomeIcon className={classes.caret} size="md" regular>
                chevron-right
              </FontAwesomeIcon>
            </Text>
          </SimpleLink>
        </div>
        <div className={classes.data}>
          <Text variant="h2" className={classes.dataLarge}>
            {volumeCounts?.detached}
          </Text>
          {volumeCounts?.detached > 0 && (
            <Text variant="body1" className={classes.dataSmall}>
              <FontAwesomeIcon className={classes.detachedIcon}>
                triangle-exclamation
              </FontAwesomeIcon>
              Not attached to VMs
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
